import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from "@/components/ui/dropdown-menu";
import { MessageSquare, GraduationCap, Users, Send, ArrowLeft, Bell, Mail, Smartphone, Clock, CheckCircle, History, Eye, Search, ChevronDown, FileText, UserCheck } from "lucide-react";
// Blue Loader component
const BlueLoader = () => (
  <div className="flex justify-center items-center py-12">
    <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-blue-500"></div>
  </div>
);
import { SidebarProvider } from "@/components/ui/sidebar";
import GuardianHeader from "./GuardianHeader";
import GuardianSidebar from "./GuardianSidebar";
import { db, auth } from "../firebase/config";
import { collection, query, where, orderBy, getDocs, addDoc, updateDoc, doc, serverTimestamp, onSnapshot, limit, writeBatch, arrayUnion } from "firebase/firestore";
import { onAuthStateChanged, getIdTokenResult } from "firebase/auth";
import { useToast } from "@/components/ui/use-toast";
import { useNavigate } from "react-router-dom";
import sanitizeHtml from 'sanitize-html';

interface CommunicationCategory {
  id: string;
  title: string;
  description: string;
  icon: React.ElementType;
  recipientCount: number;
  color: string;
  recipients: string;
  disabled?: boolean;
}

interface Memo {
  id: string;
  title: string;
  description: string;
  fileUrl: string;
  fileName: string;
  fileType: string;
  targetAudience: 'staff' | 'teacher-admin' | 'student' | 'parent' | 'everyone';
  createdAt: string;
  senderEmail: string;
  recipientCount: number;
  readBy: string[];
}

interface Conversation {
  id: string;
  title: string;
  allowedEmails: string[];
  participantRoles: string[];
  type: '1:1' | 'group';
  lastMessage: {
    content: string;
    senderEmail: string;
    timestamp: string;
  } | null;
  lastUpdatedAt: string;
  recipients: string;
  recipientCount: number;
  hasReceivedMessages: boolean;
  schoolID: string;
  unreadCount: number;
}

interface Message {
  id: string;
  content: string;
  senderEmail: string;
  timestamp: string;
  readBy: string[];
  priority: string;
  deliveryMethod: string;
}

interface User {
  id: string;
  name: string;
  email: string;
  role: string;
  subject?: string;
  studentName?: string;
}

interface Affiliation {
  role: string;
  schoolID?: string;
}

interface CustomClaims {
  affiliations?: Affiliation[];
  defaultAffiliation?: Affiliation;
  schoolID?: string;
  role?: string;
}

interface StudentData {
  registrationNumber: string;
  firstName: string;
  middleName: string;
  lastName: string;
  email: string;
  guardians: { name: string; phone: string; email: string; address: string }[];
  status: string;
  studentUid: string;
}

const ParentCommunication = () => {
  const { toast } = useToast();
  const navigate = useNavigate();
  const [activeView, setActiveView] = useState<string>("overview");
  const [selectedCategory, setSelectedCategory] = useState<CommunicationCategory | null>(null);
  const [selectedConversation, setSelectedConversation] = useState<Conversation | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [isLoadingMessages, setIsLoadingMessages] = useState(false);
  const [message, setMessage] = useState("");
  const [subject, setSubject] = useState("");
  const [priority, setPriority] = useState("normal");
  const [deliveryMethod, setDeliveryMethod] = useState("app");
  const [schoolID, setSchoolID] = useState<string>("");
  const [isAuthLoading, setIsAuthLoading] = useState(true);
  const [hasValidClaims, setHasValidClaims] = useState(false);
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [teachers, setTeachers] = useState<User[]>([]);
  const [students, setStudents] = useState<User[]>([]);
  const [selectedRecipient, setSelectedRecipient] = useState("");
  const [recipientSearch, setRecipientSearch] = useState("");
  const [recipientType, setRecipientType] = useState<"teacher" | "student" | null>(null);
  const [returnView, setReturnView] = useState<"received-messages" | "sent-messages">("received-messages");
  const [unreadCount, setUnreadCount] = useState(0);
  const [showHistory, setShowHistory] = useState(false);
  
  // Memo States
  const [activeTab, setActiveTab] = useState<"messages" | "memos">("messages");
  const [selectedMemo, setSelectedMemo] = useState<Memo | null>(null);
  const [activeMemoCategory, setActiveMemoCategory] = useState<string | null>(null);
  const [memos, setMemos] = useState<Memo[]>([]);

  const [communicationCategories, setCommunicationCategories] = useState<CommunicationCategory[]>([
    { id: "teachers", title: "Communicate with Teachers", description: "Send messages to teachers", icon: GraduationCap, recipientCount: 0, color: "bg-green-500", recipients: "Teachers" },
    { id: "students", title: "Communicate with Students", description: "Send messages to students", icon: Users, recipientCount: 0, color: "bg-orange-500", recipients: "Students" },
  ]);

  const getMessagePreview = (content: string, maxLength: number) => {
    if (content.length <= maxLength) return content;
    return content.substring(0, maxLength) + '...';
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        try {
          const idTokenResult = await getIdTokenResult(user, true);
          const claims = idTokenResult.claims as CustomClaims;
          const affiliations = claims.affiliations || [];
          const defaultAff = claims.defaultAffiliation;
          const claimSchoolID = claims.schoolID;

          const guardianAff = defaultAff?.role === "guardian" && defaultAff.schoolID
            ? defaultAff
            : affiliations.find(aff => aff.role === "guardian" && aff.schoolID);

          if (guardianAff?.schoolID && guardianAff.schoolID === claimSchoolID) {
            setSchoolID(guardianAff.schoolID);
            setHasValidClaims(true);
          } else {
            throw new Error("Invalid guardian claims");
          }
        } catch (error: any) {
          console.error("Error verifying user claims:", error);
          // Check if it's a network error
          const isNetworkError = error?.code === 'auth/network-request-failed' ||
                                error?.message?.includes('ERR_NAME_NOT_RESOLVED') ||
                                error?.message?.includes('network-request-failed') ||
                                error?.message?.includes('network');
          
          if (isNetworkError) {
            // Don't logout on network errors, just show a warning
            toast({ 
              title: "Network Error", 
              description: "Unable to verify permissions due to network issues. Please check your connection.", 
              variant: "destructive", 
              duration: 5000 
            });
          } else {
            // Only logout for non-network errors
            toast({ title: "Missing Permissions", description: "You lack permissions to access communications.", variant: "destructive", duration: 5000 });
            navigate("/login-page");
          }
        }
      } else {
        // Don't redirect here - let App.tsx global routing handle it
        // This prevents incorrect redirects during network errors
        console.log("ParentCommunication: No user found, deferring to App.tsx routing");
      }
      setIsAuthLoading(false);
    });

    return () => unsubscribe();
  }, [navigate, toast]);

  const fetchUsers = useCallback(async () => {
    if (!schoolID) return;

    try {
      // Fetch teachers
      const teachersRef = collection(db, `schools/${schoolID}/teachers`);
      const teacherSnapshot = await getDocs(teachersRef);
      const teacherList: User[] = teacherSnapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          name: `${data.firstName} ${data.lastName}`,
          email: sanitizeHtml(data.email || '', { allowedTags: [] }),
          role: 'school_staff',
          subject: data.subjectsOfExpertise?.join(', ') || 'N/A',
        };
      });

      // Fetch students
      const studentsRef = collection(db, `schools/${schoolID}/students`);
      let studentSnapshot;
      try {
        studentSnapshot = await getDocs(query(studentsRef, where("status", "==", "Active")));
      } catch (err) {
        console.warn('Query error for "Active":', err);
      }

      if (!studentSnapshot || studentSnapshot.size === 0) {
        try {
          studentSnapshot = await getDocs(query(studentsRef, where("status", "==", "active")));
        } catch (err) {
          console.warn('Query error for "active":', err);
        }
      }

      const studentList: User[] = (studentSnapshot?.docs || []).map((doc: any) => {
        const data = doc.data() as StudentData;
        return {
          id: data.studentUid || doc.id,
          name: `${data.firstName} ${data.middleName ? data.middleName + ' ' : ''}${data.lastName}`,
          email: sanitizeHtml(data.email || '', { allowedTags: [] }),
          role: 'student',
        };
      });

      setTeachers(teacherList);
      setStudents(studentList);

      setCommunicationCategories(prev => prev.map(category => ({
        ...category,
        recipientCount: category.id === "teachers" ? teacherList.length : studentList.length,
        disabled: category.id === "teachers" ? teacherList.length === 0 : studentList.length === 0,
      })));
    } catch (error) {
      console.error("Error fetching users:", error);
      toast({ title: "Error", description: "Failed to load users.", variant: "destructive", duration: 5000 });
    }
  }, [schoolID, toast]);

  const fetchConversations = useCallback(() => {
    if (!schoolID || !auth.currentUser?.email) {
      return () => {};
    }

    const convCollection = `schools/${schoolID}/conversations`;
    const q = query(
      collection(db, convCollection),
      where("allowedEmails", "array-contains", auth.currentUser.email),
      where("schoolID", "==", schoolID),
      orderBy("lastUpdatedAt", "desc"),
      limit(20)
    );

    const unsubscribe = onSnapshot(q, async (snapshot) => {
      const convList: Conversation[] = [];
      let totalUnread = 0;
      
      // Process each conversation to get unread counts
      for (const doc of snapshot.docs) {
        const data = doc.data();
        const hasReceivedMessages = data.lastMessage && data.lastMessage.senderEmail !== auth.currentUser?.email;
        
        // Count actual unread messages from the messages subcollection
        let unreadForConv = 0;
        if (hasReceivedMessages) {
          try {
            const messagesRef = collection(db, `schools/${schoolID}/conversations/${doc.id}/messages`);
            const unreadQuery = query(messagesRef, where('senderEmail', '!=', auth.currentUser?.email || ''));
            const messagesSnapshot = await getDocs(unreadQuery);
            
            messagesSnapshot.forEach((msgDoc) => {
              const msgData = msgDoc.data();
              if (!msgData.readBy?.includes(auth.currentUser?.email || '')) {
                unreadForConv++;
              }
            });
            
            totalUnread += unreadForConv;
          } catch (error) {
            console.error(`Error counting unread for conversation ${doc.id}:`, error);
          }
        }

        convList.push({
          id: doc.id,
          title: data.title || '',
          allowedEmails: data.allowedEmails || [],
          participantRoles: data.participantRoles || [],
          type: data.type || '1:1',
          lastMessage: data.lastMessage ? {
            content: data.lastMessage.content || '',
            senderEmail: data.lastMessage.senderEmail || '',
            timestamp: data.lastMessage.timestamp?.toDate()?.toLocaleString() || '',
          } : null,
          lastUpdatedAt: data.lastUpdatedAt?.toDate()?.toLocaleString() || '',
          recipients: data.recipients || '',
          recipientCount: data.recipientCount || 0,
          hasReceivedMessages,
          schoolID: data.schoolID || schoolID,
          unreadCount: unreadForConv,
        });
      }
      setConversations(convList);
      setUnreadCount(totalUnread);
    }, (error) => {
      console.error("Error fetching conversations:", error);
      toast({ title: "Error", description: "Failed to load conversations.", variant: "destructive", duration: 5000 });
    });

    return unsubscribe;
  }, [schoolID, toast]);

  const fetchMemos = useCallback(() => {
    if (!schoolID) return () => {};
    
    const memosRef = collection(db, `schools/${schoolID}/memos`);
    // Parents see 'guardians' and 'everyone' memos
    const q = query(memosRef, orderBy("createdAt", "desc"), limit(50));

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const memoList: Memo[] = snapshot.docs.map(doc => ({
        id: doc.id,
        title: doc.data().title,
        description: doc.data().description,
        fileUrl: doc.data().fileUrl,
        fileName: doc.data().fileName,
        fileType: doc.data().fileType,
        targetAudience: doc.data().targetAudience,
        createdAt: doc.data().createdAt?.toDate()?.toLocaleString() || '',
        senderEmail: doc.data().senderEmail,
        recipientCount: doc.data().recipientCount || 0,
        readBy: doc.data().readBy || [],
      }));
      // Filter relevant memos
      const relevantMemos = memoList.filter(m => ['guardians', 'everyone'].includes(m.targetAudience));
      setMemos(relevantMemos);
    });
    return unsubscribe;
  }, [schoolID]);

  const handleViewMemo = async (memo: Memo) => {
    setSelectedMemo(memo);
    const userEmail = auth.currentUser?.email;
    if (userEmail) {
      try {
        const memoRef = doc(db, `schools/${schoolID}/memos`, memo.id);
        await updateDoc(memoRef, {
          readBy: arrayUnion(userEmail)
        });
        // Optimistic update
        setMemos(prev => prev.map(m => m.id === memo.id ? { ...m, readBy: [...(m.readBy || []), userEmail] } : m));
      } catch (error) {
        console.error("Error marking memo as read:", error);
      }
    }
  };

  useEffect(() => {
    if (!schoolID || isAuthLoading || !hasValidClaims) return;

    fetchUsers();
    const unsubscribeConv = fetchConversations();
    const unsubscribeMemos = fetchMemos();
    return () => {
      unsubscribeConv();
      unsubscribeMemos();
    };
  }, [schoolID, isAuthLoading, hasValidClaims, fetchUsers, fetchConversations, fetchMemos]);

  const fetchConversationMessages = async (conversationId: string) => {
    setIsLoadingMessages(true);
    try {
      const messagesCollection = `schools/${schoolID}/conversations/${conversationId}/messages`;
      const q = query(collection(db, messagesCollection), orderBy("timestamp", "desc"), limit(50));
      const snapshot = await getDocs(q);
      const messageList: Message[] = snapshot.docs.map(doc => ({
        id: doc.id,
        content: sanitizeHtml(doc.data().content || '', { allowedTags: [] }),
        senderEmail: sanitizeHtml(doc.data().senderEmail || '', { allowedTags: [] }),
        timestamp: doc.data().timestamp?.toDate()?.toLocaleString() || '',
        readBy: doc.data().readBy || [],
        priority: doc.data().priority || 'normal',
        deliveryMethod: doc.data().deliveryMethod || 'app',
      }));

      if (auth.currentUser?.email) {
        const messagesToUpdate = messageList.filter(msg => !msg.readBy.includes(auth.currentUser?.email || ''));
        for (const msg of messagesToUpdate) {
          try {
            await updateDoc(doc(db, messagesCollection, msg.id), {
              readBy: [...msg.readBy, auth.currentUser?.email],
            });
          } catch (error) {
            console.error(`Error updating read status for message ${msg.id}:`, error);
          }
        }
      }

      setMessages(messageList);
      
      // Mark unread messages as read in Firestore
      const currentUserEmail = auth.currentUser?.email;
      if (currentUserEmail) {
        const batch = writeBatch(db);
        let hasUpdates = false;
        
        for (const msg of messageList) {
          if (msg.senderEmail !== currentUserEmail && !msg.readBy.includes(currentUserEmail)) {
            const msgRef = doc(db, `schools/${schoolID}/conversations/${conversationId}/messages`, msg.id);
            batch.update(msgRef, {
              readBy: arrayUnion(currentUserEmail)
            });
            hasUpdates = true;
          }
        }
        
        if (hasUpdates) {
          await batch.commit();
        }
      }
      
      // Update conversation unread count to 0 and recalculate total
      setConversations(prev => {
        const updated = prev.map(conv => 
          conv.id === conversationId ? { ...conv, unreadCount: 0 } : conv
        );
        const newTotal = updated.reduce((sum, conv) => sum + conv.unreadCount, 0);
        setUnreadCount(newTotal);
        return updated;
      });
    } catch (error) {
      console.error("Error fetching messages:", error);
      toast({ title: "Error", description: "Failed to load messages.", variant: "destructive", duration: 5000 });
      setMessages([]);
    } finally {
      setIsLoadingMessages(false);
    }
  };

  const handleCategorySelect = (category: CommunicationCategory) => {
    if (category.disabled) {
      toast({ title: "Error", description: `No ${category.recipients.toLowerCase()} available.`, variant: "destructive", duration: 5000 });
      return;
    }
    setSelectedCategory(category);
    setSelectedRecipient("");
    setRecipientSearch("");
    setRecipientType(category.id === "teachers" ? "teacher" : "student");
    setActiveView("compose");
  };

  const handleRecipientSelect = (recipient: User) => {
    setSelectedRecipient(recipient.email);
    setRecipientSearch("");
    setSelectedCategory({
      ...selectedCategory!,
      title: `Send to ${recipient.name}`,
      description: `Direct message to ${recipient.name} (${recipient.role === 'school_staff' ? `Teacher - ${recipient.subject}` : 'Student'})`,
      recipientCount: 1,
      recipients: `${recipient.name} - ${recipient.role === 'school_staff' ? `Teacher` : 'Student'}`,
    });
  };

  const handleSendMessage = async () => {
    if (!schoolID || !hasValidClaims || !selectedCategory || !auth.currentUser?.email) {
      toast({ title: "Error", description: "Missing permissions or invalid state.", variant: "destructive", duration: 5000 });
      return;
    }

    if (!subject || !message) {
      toast({ title: "Error", description: "Subject and message are required.", variant: "destructive", duration: 5000 });
      return;
    }

    if (!selectedRecipient) {
      toast({ title: "Error", description: "Please select a recipient.", variant: "destructive", duration: 5000 });
      return;
    }

    try {
      const convCollection = `schools/${schoolID}/conversations`;
      const recipient = recipientType === "teacher" 
        ? teachers.find(t => t.email === selectedRecipient) 
        : students.find(s => s.email === selectedRecipient);
        
      if (!recipient) throw new Error("Selected recipient not found.");

      const allowedEmails = [auth.currentUser.email, recipient.email].sort();
      const participantRoles = ['guardian', recipient.role];
      const recipients = `${recipient.name} - ${recipient.role === 'school_staff' ? `Teacher` : 'Student'}`;

      // Check for existing conversation
      const convQuery = query(
        collection(db, convCollection),
        where("type", "==", "1:1"),
        where("allowedEmails", "==", allowedEmails),
        where("schoolID", "==", schoolID)
      );
      const convSnapshot = await getDocs(convQuery);
      
      let conversationId: string;
      if (!convSnapshot.empty) {
        conversationId = convSnapshot.docs[0].id;
      } else {
        const convDoc = await addDoc(collection(db, convCollection), {
          title: selectedCategory.title,
          allowedEmails,
          participantRoles,
          type: '1:1',
          lastMessage: null,
          lastUpdatedAt: serverTimestamp(),
          recipients,
          recipientCount: 1,
          schoolID,
        });
        conversationId = convDoc.id;
      }

      const messagesCollection = `schools/${schoolID}/conversations/${conversationId}/messages`;
      const newMessage = {
        content: sanitizeHtml(`${subject}\n\n${message}`, { allowedTags: [] }),
        senderEmail: auth.currentUser.email,
        timestamp: serverTimestamp(),
        readBy: [auth.currentUser.email],
        priority,
        deliveryMethod,
      };

      await addDoc(collection(db, messagesCollection), newMessage);
      await updateDoc(doc(db, convCollection, conversationId), {
        lastMessage: {
          content: newMessage.content,
          senderEmail: auth.currentUser.email,
          timestamp: serverTimestamp(),
        },
        lastUpdatedAt: serverTimestamp(),
      });

      setMessage("");
      setSubject("");
      setPriority("normal");
      setDeliveryMethod("app");
      setSelectedRecipient("");
      setRecipientSearch("");
      setRecipientType(null);
      setActiveView("overview");
      setSelectedCategory(null);
      toast({ title: "Success", description: "Message sent successfully.", duration: 3000 });
    } catch (error: any) {
      console.error("Error sending message:", error);
      toast({
        title: error.code === "permission-denied" ? "Permission Error" : "Error",
        description: error.message || "Failed to send message.",
        variant: "destructive",
        duration: 5000,
      });
    }
  };

  const filteredRecipients = recipientType === "teacher"
    ? teachers.filter(t => t.name.toLowerCase().includes(recipientSearch.toLowerCase()) || t.email.toLowerCase().includes(recipientSearch.toLowerCase()) || t.subject?.toLowerCase().includes(recipientSearch.toLowerCase()))
    : students.filter(s => s.name.toLowerCase().includes(recipientSearch.toLowerCase()) || s.email.toLowerCase().includes(recipientSearch.toLowerCase()));

  const viewConversationDetails = (conv: Conversation, fromView: "received-messages" | "sent-messages") => {
    setSelectedConversation(conv);
    setReturnView(fromView);
    setMessages([]);
    setShowHistory(false);
    setIsLoadingMessages(true);
    fetchConversationMessages(conv.id);
    setActiveView("conversation-details");
  };

  const getSenderName = (email: string) => {
    if (!email) return "Unknown Sender";
    if (email === auth.currentUser?.email) return "Me";
    return teachers.find(t => t.email === email)?.name || 
           students.find(s => s.email === email)?.name || 
           email;
  };

  const getConversationListTitle = (conv: Conversation) => {
    if (conv.type === '1:1') {
      const otherEmail = conv.allowedEmails.find(e => e !== auth.currentUser?.email);
      if (otherEmail) {
        return getSenderName(otherEmail);
      }
    }
    return conv.title;
  };

  const getDisplayTitle = () => {
    if (selectedConversation?.type === '1:1') {
      const otherEmail = selectedConversation.allowedEmails.find(e => e !== auth.currentUser?.email);
      if (otherEmail) {
        const otherName = getSenderName(otherEmail);
        return returnView === "received-messages" ? `From ${otherName}` : `To ${otherName}`;
      }
    }
    return selectedConversation?.title || 'Conversation';
  };

  if (isAuthLoading || !schoolID || !hasValidClaims) {
    return (
      <SidebarProvider>
        <div className="flex h-screen w-full bg-gray-50 dark:bg-gray-900">
          <GuardianSidebar />
          <div className="flex flex-col flex-1">
            <GuardianHeader />
            <div className="flex-1 overflow-y-auto p-3 sm:p-6 bg-gray-50 dark:bg-gray-900 flex justify-center items-center">
              <BlueLoader />
            </div>
          </div>
        </div>
      </SidebarProvider>
    );
  }

  return (
    <SidebarProvider>
      <div className="flex h-screen w-full bg-gray-50 dark:bg-gray-900">
        <GuardianSidebar />
        <div className="flex flex-col flex-1">
          <GuardianHeader />
          <main className="flex-1 overflow-y-auto p-3 sm:p-6 bg-[#FFFFFF] dark:bg-[#020817] overflow-x-hidden">
            <div className="mb-6 flex justify-center">
              <div className="bg-gray-100 dark:bg-gray-800 p-1 rounded-lg inline-flex">
                <button
                  onClick={() => { setActiveTab("messages"); setActiveView("overview"); setSelectedCategory(null); }}
                  className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${activeTab === "messages" ? "bg-white dark:bg-gray-700 shadow-sm text-gray-900 dark:text-white" : "text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white"}`}
                >
                  Messages
                </button>
                <button
                  onClick={() => { setActiveTab("memos"); setActiveView("overview"); setSelectedCategory(null); }}
                  className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${activeTab === "memos" ? "bg-white dark:bg-gray-700 shadow-sm text-gray-900 dark:text-white" : "text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white"} flex items-center gap-2`}
                >
                  Memos
                  {memos.length > 0 && (
                    <Badge variant="secondary" className="h-5 w-5 p-0 flex items-center justify-center rounded-full text-[10px]">
                      {memos.length}
                    </Badge>
                  )}
                </button>
              </div>
            </div>

            {activeTab === "messages" ? (
              <>
            {activeView === "conversation-details" && selectedConversation ? (
              <div className="max-w-6xl mx-auto">
                <div className="mb-6 flex items-center gap-4">
                  <Button variant="outline" size="sm" onClick={() => setActiveView(returnView)} className="flex items-center gap-2">
                    <ArrowLeft className="h-4 w-4" /> Back to {returnView === "received-messages" ? "Inbox" : "Sent"}
                  </Button>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
                  <div className="lg:col-span-3 space-y-6">
                    {/* Email Subject Header */}
                    <div className="bg-white dark:bg-[#020817] p-1">
                        <h1 className="text-2xl font-semibold text-gray-900 dark:text-white mb-2">{getDisplayTitle()}</h1>
                        <div className="flex items-center gap-3 text-sm text-gray-500 dark:text-gray-400">
                            <Badge variant="secondary" className="rounded-md font-normal">{selectedConversation.type === '1:1' ? 'Direct Message' : 'Group Announcement'}</Badge>
                            <span>&bull;</span>
                            <span>{selectedConversation.recipients}</span>
                        </div>
                    </div>

                    {/* Message Thread */}
                    <div className="space-y-4">
                        {isLoadingMessages ? (
                          <div className="flex justify-center py-12">
                            <BlueLoader />
                          </div>
                        ) : messages.length === 0 ? (
                          <div className="text-center py-12 bg-white dark:bg-[#020817] rounded-lg border border-dashed border-gray-200 dark:border-gray-800">
                            <p className="text-gray-500">No messages in this conversation.</p>
                          </div>
                        ) : (
                          <>
                            {(() => {
                              const unreadMessages = messages.filter(m => !m.readBy.includes(auth.currentUser?.email || ''));
                              let displayedMessages = messages;
                              
                              if (!showHistory) {
                                if (unreadMessages.length > 0) {
                                  displayedMessages = unreadMessages;
                                } else {
                                  displayedMessages = [messages[0]];
                                }
                              }

                              return (
                                <>
                                  {displayedMessages.map((msg) => {
                                    const senderName = getSenderName(msg.senderEmail);
                                    const isMe = msg.senderEmail === auth.currentUser?.email;
                                    
                                    let displaySubject = "No Subject";
                                    let displayBody = msg.content;
                                    
                                    const parts = msg.content.split('\n\n');
                                    if (parts.length >= 2) {
                                      displaySubject = parts[0];
                                      displayBody = parts.slice(1).join('\n\n');
                                    } else {
                                        const partsSingle = msg.content.split('\n');
                                        if (partsSingle.length >= 2) {
                                            displaySubject = partsSingle[0];
                                            displayBody = partsSingle.slice(1).join('\n');
                                        }
                                    }

                                    return (
                                      <Card key={msg.id} className="border border-gray-200 dark:border-gray-800 shadow-sm overflow-hidden bg-white dark:bg-[#020817] mb-6">
                                        <div className="p-0">
                                            <div className="bg-gray-50/80 dark:bg-gray-900/50 p-6 border-b border-gray-100 dark:border-gray-800 space-y-3">
                                                <div className="grid grid-cols-[60px_1fr] gap-2 items-baseline">
                                                    <span className="text-xs font-bold text-gray-500 uppercase tracking-wider">To</span>
                                                    <span className="text-sm font-medium text-gray-900 dark:text-gray-200">{isMe ? selectedConversation.recipients : 'Me'}</span>
                                                </div>
                                                <div className="grid grid-cols-[60px_1fr] gap-2 items-baseline">
                                                    <span className="text-xs font-bold text-gray-500 uppercase tracking-wider">From</span>
                                                    <span className="text-sm font-medium text-gray-900 dark:text-gray-200">{senderName} &lt;{msg.senderEmail}&gt;</span>
                                                </div>
                                                <div className="grid grid-cols-[60px_1fr] gap-2 items-baseline">
                                                    <span className="text-xs font-bold text-gray-500 uppercase tracking-wider">Date</span>
                                                    <span className="text-sm text-gray-600 dark:text-gray-400">{msg.timestamp}</span>
                                                </div>
                                                <div className="grid grid-cols-[60px_1fr] gap-2 items-baseline">
                                                    <span className="text-xs font-bold text-gray-500 uppercase tracking-wider">Subject</span>
                                                    <span className="text-sm font-semibold text-gray-900 dark:text-white">{displaySubject}</span>
                                                </div>
                                            </div>
                                            
                                            <div className="p-6 sm:p-8">
                                                <div className="text-base leading-7 text-gray-700 dark:text-gray-300 font-normal whitespace-pre-wrap break-words font-sans">
                                                    {displayBody}
                                                </div>
                                            </div>
                                            
                                            {isMe && (
                                                <div className="px-6 py-3 bg-gray-50/50 dark:bg-gray-900/30 border-t border-gray-100 dark:border-gray-800 flex justify-end">
                                                    <div className="text-xs font-medium text-gray-500 flex items-center gap-1.5">
                                                        {msg.readBy.length > 1 ? (
                                                            <>
                                                                <CheckCircle className="h-3.5 w-3.5 text-green-600" />
                                                                <span className="text-green-700 dark:text-green-500">Read by {msg.readBy.length - 1} recipient(s)</span>
                                                            </>
                                                        ) : (
                                                            <span>Sent</span>
                                                        )}
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                      </Card>
                                    );
                                  })}
                                  
                                  {!showHistory && messages.length > displayedMessages.length && (
                                    <div className="flex justify-center pt-4">
                                      <Button 
                                        variant="outline" 
                                        onClick={() => setShowHistory(true)}
                                        className="flex items-center gap-2 text-gray-500"
                                      >
                                        <History className="h-4 w-4" />
                                        Show Previous Messages ({messages.length - displayedMessages.length})
                                      </Button>
                                    </div>
                                  )}
                                </>
                              );
                            })()}
                          </>
                        )}
                    </div>
                  </div>

                  {/* Sidebar Context */}
                  <div className="space-y-6 lg:sticky lg:top-6 lg:self-start">
                    <Card className="border border-gray-200 dark:border-gray-800 bg-white dark:bg-[#020817] shadow-sm">
                      <CardHeader className="pb-3 border-b border-gray-100 dark:border-gray-800">
                        <CardTitle className="text-base font-medium">About this conversation</CardTitle>
                      </CardHeader>
                      <CardContent className="pt-4 space-y-4">
                        <div>
                            <div className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">Participants</div>
                            <div className="flex items-center justify-between">
                                <span className="text-sm text-gray-700 dark:text-gray-300">Total Recipients</span>
                                <Badge variant="secondary" className="font-normal">{selectedConversation.recipientCount}</Badge>
                            </div>
                        </div>
                        
                        <div>
                            <div className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">Roles Involved</div>
                            <div className="flex flex-wrap gap-1.5">
                              {selectedConversation.participantRoles.map((role, index) => (
                                <Badge key={index} variant="outline" className="text-xs font-normal capitalize bg-gray-50 dark:bg-gray-900">{role.replace('_', ' ')}</Badge>
                              ))}
                            </div>
                        </div>
                      </CardContent>
                    </Card>
                  </div>
                </div>
              </div>
            ) : activeView === "received-messages" ? (
              <div className="max-w-6xl mx-auto">
                <div className="mb-6 flex items-center gap-4">
                  <Button variant="outline" size="sm" onClick={() => setActiveView("overview")} className="flex items-center gap-2">
                    <ArrowLeft className="h-4 w-4" /> Back
                  </Button>
                  <div>
                    <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Received Messages</h1>
                    <p className="text-gray-600 dark:text-gray-400">Messages sent to you</p>
                  </div>
                </div>
                <Card className="m-2 border border-[#E2E8F0] dark:border-[#1E293B] bg-[#FFFFFF] dark:bg-[#020817]">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2"><Mail className="h-5 w-5" /> Received</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {conversations.filter(conv => conv.hasReceivedMessages).length === 0 ? (
                      <p className="text-center text-gray-500 dark:text-gray-400 py-4">No messages received yet.</p>
                    ) : (
                      <div className="space-y-4">
                        {conversations.filter(conv => conv.hasReceivedMessages).map(conv => (
                        <div
                          key={conv.id}
                          className="m-2 border border-[#E2E8F0] dark:border-[#1E293B] rounded-lg hover:bg-gray-50 dark:hover:bg-[#2C2C3B] cursor-pointer transition-colors"
                          onClick={() => viewConversationDetails(conv, "received-messages")}
                        >
                          <div className="flex items-center justify-between p-4">
                            <div className="flex items-center gap-3 flex-1 min-w-0">
                              <div className="flex-1 min-w-0">
                                <h4 className="font-medium truncate">{getConversationListTitle(conv)}</h4>
                                <p className="text-sm text-gray-600 dark:text-gray-400 truncate">{conv.recipients} • {conv.lastUpdatedAt}</p>
                                {conv.lastMessage && (
                                  <p className="text-xs text-gray-500 truncate">{getMessagePreview(conv.lastMessage.content, 50)}</p>
                                )}
                              </div>
                            </div>
                            <div className="flex items-center gap-2 flex-shrink-0">
                              {conv.unreadCount > 0 && (
                                <Badge variant="destructive" className="text-xs">{conv.unreadCount} New</Badge>
                              )}
                              <Button variant="outline" size="sm" onClick={(e) => { e.stopPropagation(); viewConversationDetails(conv, "received-messages"); }}>
                                <Eye className="h-3 w-3 mr-1" /> <span className="hidden sm:inline">View</span>
                              </Button>
                            </div>
                          </div>
                        </div>
                      ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>
            ) : activeView === "sent-messages" ? (
              <div className="max-w-6xl mx-auto">
                <div className="mb-6 flex items-center gap-4">
                  <Button variant="outline" size="sm" onClick={() => setActiveView("overview")} className="flex items-center gap-2">
                    <ArrowLeft className="h-4 w-4" /> Back
                  </Button>
                  <div>
                    <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Sent Messages</h1>
                  </div>
                </div>
                <Card className="m-2 border border-[#E2E8F0] dark:border-[#1E293B] bg-[#FFFFFF] dark:bg-[#020817]">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2"><History className="h-5 w-5" /> Sent</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {conversations.filter(conv => conv.lastMessage && conv.lastMessage.senderEmail === auth.currentUser?.email).length === 0 ? (
                      <p className="text-center text-gray-500 dark:text-gray-400 py-4">No messages sent yet.</p>
                    ) : (
                      conversations.filter(conv => conv.lastMessage && conv.lastMessage.senderEmail === auth.currentUser?.email).map((conv) => (
                        <div
                          key={conv.id}
                          className="m-2 border border-[#E2E8F0] dark:border-[#1E293B] rounded-lg hover:bg-gray-50 dark:hover:bg-[#2C2C3B] cursor-pointer"
                          onClick={() => viewConversationDetails(conv, "sent-messages")}
                        >
                          <div className="flex items-center justify-between p-4">
                            <div className="flex items-center gap-3 flex-1 min-w-0">
                              <div className="flex-1 min-w-0">
                                <h4 className="font-medium truncate">{conv.title}</h4>
                                <p className="text-sm text-gray-600 dark:text-gray-400 truncate">To: {conv.recipients} • {conv.lastUpdatedAt}</p>
                              </div>
                            </div>
                            <Button variant="outline" size="sm" onClick={(e) => { e.stopPropagation(); viewConversationDetails(conv, "sent-messages"); }} className="flex-shrink-0">
                              <Eye className="h-3 w-3 mr-1" /> <span className="hidden sm:inline">View</span>
                            </Button>
                          </div>
                        </div>
                      ))
                    )}
                  </CardContent>
                </Card>
              </div>
            ) : activeView === "compose" && selectedCategory ? (
              <div className="max-w-4xl mx-auto">
                <div className="mb-6 flex items-center gap-4">
                  <Button variant="outline" size="sm" onClick={() => setActiveView("overview")} className="flex items-center gap-2">
                    <ArrowLeft className="h-4 w-4" /> Back
                  </Button>
                  <div>
                    <h1 className="text-2xl font-bold text-gray-900 dark:text-white">{selectedCategory.title}</h1>
                  </div>
                </div>
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                  <div className="lg:col-span-2">
                    <Card className="m-2 border border-[#E2E8F0] dark:border-[#1E293B] bg-[#FFFFFF] dark:bg-[#020817]">
                      <CardHeader>
                        <CardTitle className="flex items-center gap-2">{React.createElement(selectedCategory.icon, { className: "h-5 w-5" })} Compose</CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-4">
                        <div>
                          <label className="block text-sm font-medium mb-2">Recipient</label>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="outline" size="sm" className="w-full justify-between">
                                {selectedRecipient || `Select ${recipientType}`}
                                <ChevronDown className="h-3 w-3" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent className="w-full min-w-[300px]">
                              <div className="p-2">
                                <div className="relative">
                                  <Search className="absolute left-2 top-2.5 h-3 w-3 text-gray-400" />
                                  <Input
                                    placeholder={`Search ${recipientType}s...`}
                                    value={recipientSearch}
                                    onChange={(e) => setRecipientSearch(sanitizeHtml(e.target.value, { allowedTags: [] }))}
                                    className="pl-7 text-xs h-8"
                                  />
                                </div>
                              </div>
                              <DropdownMenuSeparator />
                              <div className="max-h-40 overflow-y-auto">
                                {filteredRecipients.length > 0 ? (
                                  filteredRecipients.map((recipient) => (
                                    <DropdownMenuItem
                                      key={recipient.email}
                                      onClick={() => handleRecipientSelect(recipient)}
                                      className="flex flex-col items-start p-2"
                                    >
                                      <div className="font-medium text-xs">{recipient.name}</div>
                                      <div className="text-xs text-gray-500">
                                        {recipientType === "teacher" ? `${recipient.subject} • ${recipient.email}` : recipient.email}
                                      </div>
                                    </DropdownMenuItem>
                                  ))
                                ) : (
                                  <DropdownMenuItem disabled className="text-center py-3 text-gray-500 text-xs">
                                    No {recipientType}s found
                                  </DropdownMenuItem>
                                )}
                              </div>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </div>
                        <div>
                          <label className="block text-sm font-medium mb-2">Subject</label>
                          <Input value={subject} onChange={(e) => setSubject(sanitizeHtml(e.target.value, { allowedTags: [] }))} placeholder="Enter subject..." />
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-medium mb-2">Priority</label>
                            <Select value={priority} onValueChange={setPriority}>
                              <SelectTrigger><SelectValue /></SelectTrigger>
                              <SelectContent>
                                <SelectItem value="low">Low</SelectItem>
                                <SelectItem value="normal">Normal</SelectItem>
                                <SelectItem value="high">High</SelectItem>
                                <SelectItem value="urgent">Urgent</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                          <div>
                            <label className="block text-sm font-medium mb-2">Delivery</label>
                            <Select value={deliveryMethod} onValueChange={setDeliveryMethod}>
                              <SelectTrigger><SelectValue /></SelectTrigger>
                              <SelectContent>
                                <SelectItem value="app">App</SelectItem>
                                <SelectItem value="email">Email</SelectItem>
                                <SelectItem value="sms">SMS</SelectItem>
                                <SelectItem value="all">All</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                        </div>
                        <div>
                          <label className="block text-sm font-medium mb-2">Message</label>
                          <Textarea value={message} onChange={(e) => setMessage(sanitizeHtml(e.target.value, { allowedTags: [] }))} placeholder="Type message..." className="min-h-[120px]" />
                        </div>
                        <div className="flex justify-end gap-3">
                          <Button variant="outline" onClick={() => setActiveView("overview")}>Cancel</Button>
                          <Button onClick={handleSendMessage} disabled={!subject || !message || !selectedRecipient}>
                            <Send className="h-4 w-4 mr-2" /> Send
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  </div>
                  <div className="space-y-6">
                    <Card className="m-2 border border-[#E2E8F0] dark:border-[#1E293B] bg-[#FFFFFF] dark:bg-[#020817]">
                      <CardHeader><CardTitle>Delivery Preview</CardTitle></CardHeader>
                      <CardContent className="space-y-3">
                        <div className="flex items-center gap-2">
                          {deliveryMethod === "app" && <Bell className="h-4 w-4" />}
                          {deliveryMethod === "email" && <Mail className="h-4 w-4" />}
                          {deliveryMethod === "sms" && <Smartphone className="h-4 w-4" />}
                          {deliveryMethod === "all" && <Users className="h-4 w-4" />}
                          <span className="text-sm capitalize">{deliveryMethod}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Clock className="h-4 w-4 text-gray-500" />
                          <span className="text-sm text-gray-600 dark:text-gray-400">Est. 2-5 min</span>
                        </div>
                        {priority === "urgent" && <Badge variant="destructive">Urgent</Badge>}
                      </CardContent>
                    </Card>
                  </div>
                </div>
              </div>
            ) : (
              <div className="max-w-6xl mx-auto">
                <div className="mb-6 flex items-center justify-between">
                  <div>
                    <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Parent Communication Center</h1>
                    <p className="text-gray-600 dark:text-gray-400">Send and manage messages</p>
                  </div>
                  <div className="flex gap-2">
                    <Button onClick={() => setActiveView("received-messages")} variant="outline" className="flex items-center gap-2">
                      <Mail className="h-4 w-4" /> Received
                      {unreadCount > 0 && (
                        <Badge variant="destructive" className="ml-1">{unreadCount}</Badge>
                      )}
                    </Button>
                    <Button onClick={() => setActiveView("sent-messages")} variant="outline">
                      <History className="h-4 w-4 mr-2" /> Sent
                    </Button>
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                  {communicationCategories.map((category) => (
                    <Card
                      key={category.id}
                      className={`border border-[#E2E8F0] dark:border-[#1E293B] hover:shadow-lg transition-all cursor-pointer border-l-4 bg-[#FFFFFF] dark:bg-[#020817] ${category.disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
                      style={{ borderLeftColor: category.color === 'bg-green-500' ? '#10b981' : '#f97316' }}
                      onClick={() => handleCategorySelect(category)}
                    >
                      <CardHeader className="pb-3">
                        <div className="flex items-center gap-3">
                          <div className={`p-2 ${category.color} rounded-lg`}>{React.createElement(category.icon, { className: "h-5 w-5 text-white" })}</div>
                          <div>
                            <CardTitle className="text-lg">{category.title}</CardTitle>
                            <CardDescription className="text-sm">{category.description}</CardDescription>
                          </div>
                        </div>
                      </CardHeader>
                      <CardContent>
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-gray-600 dark:text-gray-400">{category.recipients}</span>
                          <Badge 
                            variant="outline" 
                            className="text-xs w-fit border border-gray-300 dark:border-gray-700 bg-white/70 dark:bg-[#181C20]/70 shadow-sm rounded-md px-3 py-1"
                          >
                            {category.recipientCount} available
                          </Badge>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
                <Card className="m-2 border border-[#E2E8F0] dark:border-[#1E293B] bg-[#FFFFFF] dark:bg-[#020817]">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2"><MessageSquare className="h-5 w-5" /> Recent</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {conversations.length === 0 ? (
                      <p className="text-center text-gray-500 dark:text-gray-400 py-4">No recent messages.</p>
                    ) : (
                      conversations.slice(0, 5).map((conv) => (
                        <div key={conv.id} className="m-2 border border-[#E2E8F0] dark:border-[#1E293B] rounded-lg hover:bg-gray-50 dark:hover:bg-[#2C2C3B]">
                          <div className="flex items-center justify-between p-4">
                            <div className="flex items-center gap-3 flex-1 min-w-0">
                              <CheckCircle className="h-4 w-4 text-green-500 flex-shrink-0" />
                              <div className="flex-1 min-w-0">
                                <h4 className="font-medium truncate">{conv.title}</h4>
                                <p className="text-sm text-gray-600 dark:text-gray-400 truncate">{conv.recipients} • {conv.lastUpdatedAt}</p>
                              </div>
                            </div>
                          </div>
                        </div>
                      ))
                    )}
                  </CardContent>
                </Card>
              </div>
            )}
            </>
            ) : (
              selectedMemo ? (
                <div className="max-w-6xl mx-auto h-[calc(100vh-140px)] flex flex-col">
                  <div className="mb-4 flex items-center gap-4 flex-shrink-0">
                    <Button variant="outline" size="sm" onClick={() => setSelectedMemo(null)} className="flex items-center gap-2">
                      <ArrowLeft className="h-4 w-4" /> Back to List
                    </Button>
                    <div>
                      <h1 className="text-xl font-bold text-gray-900 dark:text-white line-clamp-1">{selectedMemo.title}</h1>
                      <p className="text-sm text-gray-500 dark:text-gray-400 line-clamp-1">{selectedMemo.description}</p>
                    </div>
                  </div>
                  <div className="flex-1 bg-white dark:bg-[#020817] rounded-lg border border-gray-200 dark:border-gray-800 overflow-hidden shadow-sm relative">
                     {selectedMemo.fileUrl ? (
                       <iframe src={selectedMemo.fileUrl} className="w-full h-full" title={selectedMemo.title} />
                     ) : (
                       <div className="flex items-center justify-center h-full text-gray-500">No file available</div>
                     )}
                  </div>
                </div>
              ) : activeMemoCategory ? (
                <div className="max-w-6xl mx-auto">
                  <div className="mb-6 flex items-center gap-4">
                    <Button variant="outline" size="sm" onClick={() => setActiveMemoCategory(null)} className="flex items-center gap-2">
                      <ArrowLeft className="h-4 w-4" /> Back to Categories
                    </Button>
                    <div>
                      <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
                        {activeMemoCategory === 'parent' ? 'Parent Memos' : 'Whole-School Memos'}
                      </h1>
                      <p className="text-gray-600 dark:text-gray-400">
                        {activeMemoCategory === 'parent' ? 'Communication for parents' : 'General notices'}
                      </p>
                    </div>
                  </div>
                  
                  <div className="grid gap-4">
                    {memos.filter(m => m.targetAudience === activeMemoCategory).length === 0 ? (
                       <div className="text-center py-12 bg-white dark:bg-[#020817] rounded-lg border border-dashed border-gray-200 dark:border-gray-800">
                         <p className="text-gray-500">No memos found in this category.</p>
                       </div>
                    ) : (
                       memos.filter(m => m.targetAudience === activeMemoCategory).map((memo) => (
                         <div key={memo.id} className="group flex items-center justify-between p-4 bg-white dark:bg-[#020817] border border-gray-200 dark:border-gray-800 rounded-lg hover:shadow-md transition-all cursor-pointer" onClick={() => handleViewMemo(memo)}>
                           <div className="flex items-center gap-4 min-w-0">
                             <div className="p-2 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                               <FileText className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                             </div>
                             <div className="min-w-0">
                               <div className="flex items-center gap-2">
                                 <h4 className="font-medium text-gray-900 dark:text-white truncate">{memo.title}</h4>
                                 {!memo.readBy?.includes(auth.currentUser?.email || '') && (
                                   <Badge variant="destructive" className="h-2 w-2 rounded-full p-0" />
                                 )}
                               </div>
                               <p className="text-sm text-gray-500 dark:text-gray-400 truncate">{memo.description}</p>
                               <div className="flex items-center gap-2 mt-1 text-xs text-gray-400">
                                 <span>{memo.createdAt}</span>
                                 <span>•</span>
                                 <span>{memo.fileName}</span>
                               </div>
                             </div>
                           </div>
                           <Button variant="ghost" size="icon" className="text-gray-400 group-hover:text-blue-600">
                             <ChevronDown className="h-4 w-4 -rotate-90" />
                           </Button>
                         </div>
                       ))
                    )}
                  </div>
                </div>
              ) : (
                <div className="max-w-6xl mx-auto">
                  <div className="mb-8">
                    <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">School Administration</h1>
                    <p className="text-gray-600 dark:text-gray-400">View official communications and memos</p>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <Card 
                      className="border border-gray-200 dark:border-gray-800 hover:shadow-lg transition-all cursor-pointer group overflow-hidden relative"
                      onClick={() => setActiveMemoCategory('parent')}
                    >
                      <div className="absolute top-0 left-0 w-1 h-full bg-purple-500" />
                      <CardHeader>
                        <div className="flex items-center gap-3 mb-2">
                          <div className="p-2 bg-purple-100 dark:bg-purple-900/20 rounded-lg group-hover:scale-110 transition-transform">
                            <UserCheck className="h-6 w-6 text-purple-600 dark:text-purple-400" />
                          </div>
                          <CardTitle>Parent Memos</CardTitle>
                        </div>
                        <CardDescription>Specific updates for parents and guardians</CardDescription>
                      </CardHeader>
                      <CardContent>
                        <div className="flex items-center justify-between text-sm text-gray-500">
                          <span>{memos.filter(m => m.targetAudience === 'parent').length} Memos</span>
                          {memos.filter(m => m.targetAudience === 'parent' && !m.readBy?.includes(auth.currentUser?.email || '')).length > 0 && (
                            <Badge variant="destructive" className="rounded-full px-2">
                              {memos.filter(m => m.targetAudience === 'parent' && !m.readBy?.includes(auth.currentUser?.email || '')).length} New
                            </Badge>
                          )}
                        </div>
                      </CardContent>
                    </Card>

                    <Card 
                      className="border border-gray-200 dark:border-gray-800 hover:shadow-lg transition-all cursor-pointer group overflow-hidden relative"
                      onClick={() => setActiveMemoCategory('everyone')}
                    >
                      <div className="absolute top-0 left-0 w-1 h-full bg-gray-500" />
                      <CardHeader>
                        <div className="flex items-center gap-3 mb-2">
                          <div className="p-2 bg-gray-100 dark:bg-gray-800 rounded-lg group-hover:scale-110 transition-transform">
                            <Users className="h-6 w-6 text-gray-600 dark:text-gray-400" />
                          </div>
                          <CardTitle>Whole-School Memos</CardTitle>
                        </div>
                        <CardDescription>General announcements for everyone</CardDescription>
                      </CardHeader>
                      <CardContent>
                        <div className="flex items-center justify-between text-sm text-gray-500">
                          <span>{memos.filter(m => m.targetAudience === 'everyone').length} Memos</span>
                          {memos.filter(m => m.targetAudience === 'everyone' && !m.readBy?.includes(auth.currentUser?.email || '')).length > 0 && (
                            <Badge variant="destructive" className="rounded-full px-2">
                              {memos.filter(m => m.targetAudience === 'everyone' && !m.readBy?.includes(auth.currentUser?.email || '')).length} New
                            </Badge>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  </div>
                </div>
              )
            )}
          </main>
        </div>
      </div>
    </SidebarProvider>
  );
};

export default ParentCommunication;
